import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { downloadBlob, fetchBlobFromApi, fetchFromApi } from 'api/apiClient';
import { formatDate } from 'components/darwin-ui';
import { useUI } from 'contexts/UiContext';
import { format } from 'date-fns';
import { SortOrder } from 'hooks/useTableControls';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { PaginationQueryParams } from 'types/queryTypes';
import { translateFormSelect } from 'utils/common';
import {
  FieldKind,
  FilterKey,
  GenerateReportParams,
  RawTransactionDetailsData,
  Report,
  TransactionDetailsResponseUI,
  UIItem,
  ReportsFiltersResponse,
} from 'types/reportTypes';

const REPORTS_QUERY_KEY = 'reports';
const REPORTS_QUERY_DATA_FILTERED = 'dataFiltered';
const REPORTS_FILTERS_QUERY_KEY = 'reports-filters';
const REACT_APP_DARWIN_URL = process.env.REACT_APP_DARWIN_URL;

export const useGetReports = ({
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'id', order: 'ASC' },
  event = '',
  products = [],
  lotCode = [],
  refDocType = '',
  refDocNumber = [],
  since = '',
  until = '',
}: PaginationQueryParams & {
  sortOrder: SortOrder;
} & {
  event?: string;
  products?: string[];
  lotCode?: string[];
  refDocType?: string;
  refDocNumber?: string[];
  since?: string;
  until?: string;
}) => {
  const { selectedCompany } = useUI();
  const filtersParams = new URLSearchParams();

  if (refDocType) {
    filtersParams.append('refDocType', refDocType);
  }
  products.forEach(product => filtersParams.append('products', product));
  lotCode.forEach(code => filtersParams.append('lotCode', code));
  refDocNumber.forEach(number => filtersParams.append('refDocNumber', number));
  if (since) {
    filtersParams.append('since', format(new Date(since), 'yyyy-MM-dd'));
  }
  if (until) {
    filtersParams.append('until', format(new Date(until), 'yyyy-MM-dd'));
  }
  if (event) {
    filtersParams.append('event', event);
  }

  return useQuery({
    queryKey: [
      REPORTS_QUERY_KEY,
      limit,
      page,
      query,
      sortOrder,
      event,
      products,
      lotCode,
      refDocType,
      refDocNumber,
      since,
      until,
    ],
    queryFn: () =>
      fetchFromApi<Report[]>(
        `${REACT_APP_DARWIN_URL}/inventories/reports/${selectedCompany}?limit=${limit}&page=${page}&filter=${query}&sortBy=${
          sortOrder.sortBy
        }&order=${sortOrder.order}&${filtersParams.toString()}`
      ),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });
};

export const useGetReportFilterOptions = ({
  query = '',
  filterType,
  limit = 50,
}: {
  query?: string;
  filterType?: FilterKey;
  limit?: number;
}) => {
  const { selectedCompany } = useUI();
  const REACT_APP_DARWIN_URL = process.env.REACT_APP_DARWIN_URL;

  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, filterType, query, limit],
    queryFn: () =>
      fetchFromApi<
        {
          transactionId: string;
          refDocNumber?: string;
          lotCode?: string;
          traceabilityId?: string;
        }[]
      >(
        `${REACT_APP_DARWIN_URL}/inventories/reports/${selectedCompany}/filter-options?${
          filterType ? `filterType=${filterType}&` : ''
        }search=${query}&limit=${limit}`
      ),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });
};

export const useGetReportsWithFilters = ({
  transactionId = undefined,
}: {
  transactionId: string | undefined;
}) => {
  const { selectedCompany } = useUI();

  return useQuery({
    queryKey: [REPORTS_QUERY_DATA_FILTERED, transactionId],
    queryFn: () =>
      fetchFromApi<Report[]>(
        `${REACT_APP_DARWIN_URL}/inventories/${selectedCompany}/transaction/${transactionId}/chain?all=true`
      ),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany && !!transactionId,
  });
};

const item = (
  key: string,
  value: string,
  kind: FieldKind = 'text',
  href?: string
): UIItem => ({
  key,
  value,
  kind,
  href,
});

const transformTransactionDetails = (
  data: RawTransactionDetailsData,
  t: TFunction<'translation', undefined>
): TransactionDetailsResponseUI => {
  return {
    firstColumn: {
      title: 'reports.transaction-details-modal.cards.product.title',
      items: [
        item(
          'reports.transaction-details-modal.cards.product.fields.product-name',
          data.product.productName
        ),
        item(
          'reports.transaction-details-modal.cards.product.fields.commodity',
          data.product.commodity
        ),
        item(
          'reports.transaction-details-modal.cards.product.fields.variety',
          data.product.variety
        ),
        item(
          'reports.transaction-details-modal.cards.product.fields.product-id',
          data.product.productId
        ),
        item(
          'reports.transaction-details-modal.cards.product.fields.sku',
          data.product.sku
        ),
        item(
          'reports.transaction-details-modal.cards.product.fields.gtin',
          data.product.gtin
        ),
        item(
          'reports.transaction-details-modal.cards.product.fields.description',
          data.product.description
        ),
        item(
          'reports.transaction-details-modal.cards.product.fields.quantity',
          data.product.quantity.toString()
        ),
        item(
          'reports.transaction-details-modal.cards.product.fields.unit-of-measure',
          data.product.unitOfMeasure,
          'uom'
        ),
        item(
          'reports.transaction-details-modal.cards.product.fields.balance',
          data.product.balance.toString()
        ),
      ],
    },
    secondColumn: [
      {
        title: 'reports.transaction-details-modal.cards.general-info.title',
        items: [
          item(
            'reports.transaction-details-modal.cards.general-info.fields.traceability-id',
            data.generalInfo.traceabilityId
          ),
          item(
            'reports.transaction-details-modal.cards.general-info.fields.transaction-date',
            data.generalInfo.transactionDate,
            'date'
          ),
          item(
            'reports.transaction-details-modal.cards.general-info.fields.created-by',
            data.generalInfo.createdBy
          ),
          item(
            'reports.transaction-details-modal.cards.general-info.fields.email',
            data.generalInfo.email
          ),
          item(
            'reports.transaction-details-modal.cards.general-info.fields.event',
            data.generalInfo.event
          ),
          item(
            'reports.transaction-details-modal.cards.general-info.fields.traceability-lot-code',
            data.generalInfo.traceabilityLotCode
          ),
        ],
      },
      {
        title: 'reports.transaction-details-modal.cards.process.title',
        items: [
          item(
            'reports.transaction-details-modal.cards.process.fields.process-name',
            data.process.processName
          ),
          item(
            'reports.transaction-details-modal.cards.process.fields.form-name',
            data.process.formName
          ),
          item(
            'reports.transaction-details-modal.cards.process.fields.registered-date',
            data.process.registeredDate,
            'date'
          ),
          item(
            'reports.transaction-details-modal.cards.process.fields.location',
            data.process.location
          ),
          item(
            'reports.transaction-details-modal.cards.process.fields.organization-name',
            data.process.organizationName
          ),
          item(
            'reports.transaction-details-modal.cards.process.fields.organization-fiscal-id',
            data.process.organizationFiscalId
          ),
          item(
            'reports.transaction-details-modal.cards.process.fields.organization-address',
            data.process.organizationAddress
          ),
        ],
      },
      {
        title: 'reports.transaction-details-modal.cards.destination.title',
        items: [
          item(
            'reports.transaction-details-modal.cards.destination.fields.recipient-location',
            data.destination.destinationLocation
          ),
          item(
            'reports.transaction-details-modal.cards.destination.fields.reference-document-type',
            data.destination.referenceDocTypeTranslationKey
              ? t(data.destination.referenceDocTypeTranslationKey)
              : translateFormSelect(t, data.destination.referenceDocType),
            'invoice'
          ),
          item(
            'reports.transaction-details-modal.cards.destination.fields.reference-document-number',
            data.destination.referenceDocNumber
          ),
        ],
      },
      {
        title:
          'reports.transaction-details-modal.cards.previous-transaction-details.title',
        items: data.previousTransactionDetails.flatMap(prev => [
          item(
            'reports.transaction-details-modal.cards.previous-transaction-details.fields.traceability-id',
            prev.previousTraceabilityId
          ),
          item(
            'reports.transaction-details-modal.cards.previous-transaction-details.fields.quantity',
            prev.quantity.toString()
          ),
          item(
            'reports.transaction-details-modal.cards.previous-transaction-details.fields.unit-of-measure',
            prev.unitOfMeasure,
            'uom'
          ),
        ]),
      },
      ...(data.additionalFields && data.additionalFields.length > 0
        ? [
            {
              title:
                'reports.transaction-details-modal.cards.additional-fields.title',
              items: data.additionalFields.flatMap(field => [
                item(
                  'reports.transaction-details-modal.cards.additional-fields.fields.name',
                  field.name
                ),
                item(
                  'reports.transaction-details-modal.cards.additional-fields.fields.value',
                  field.value,
                  'text',
                  field.downloadUrl
                ),
              ]),
            },
          ]
        : []),
      ...(data.addedProducts && data.addedProducts.length > 0
        ? [
            {
              title:
                'reports.transaction-details-modal.cards.added-products.title',
              items: data.addedProducts.flatMap(field => [
                item(
                  'reports.transaction-details-modal.cards.added-products.fields.id',
                  field.productId
                ),
                item(
                  'reports.transaction-details-modal.cards.added-products.fields.name',
                  field.productName
                ),
                item(
                  'reports.transaction-details-modal.cards.added-products.fields.commodity',
                  field.commodity
                ),
                item(
                  'reports.transaction-details-modal.cards.added-products.fields.variety',
                  field.variety
                ),
                item(
                  'reports.transaction-details-modal.cards.added-products.fields.unit-of-measure',
                  t(
                    `template-select.${field.unitOfMeasure
                      .toLowerCase()
                      .replaceAll(' ', '-')}`
                  )
                ),
                item(
                  'reports.transaction-details-modal.cards.added-products.fields.quantity',
                  field.quantity.toString()
                ),
              ]),
            },
          ]
        : []),
    ],
  };
};

export const useGetTransactionDetails = ({
  transactionId,
  t,
}: {
  transactionId: string | undefined;
  t: TFunction<'translation', undefined>;
}) => {
  const { selectedCompany } = useUI();

  return useQuery<
    RawTransactionDetailsData,
    Error,
    TransactionDetailsResponseUI
  >({
    queryKey: [REPORTS_QUERY_KEY, 'transactionDetails', transactionId],
    queryFn: async () =>
      (
        await fetchFromApi<RawTransactionDetailsData>(
          `${REACT_APP_DARWIN_URL}/inventories/${selectedCompany}/transaction/${transactionId}/details`
        )
      ).data,
    select: data => transformTransactionDetails(data, t),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany && !!transactionId,
  });
};

export const useDownloadTransactionsReport = () => {
  const { selectedCompany } = useUI();
  const { i18n } = useTranslation();

  return useMutation({
    mutationFn: async (params: GenerateReportParams): Promise<Blob> => {
      const queryParams = new URLSearchParams();

      if (params.format) queryParams.append('format', params.format);
      if (params.since) queryParams.append('since', params.since);
      if (params.until) queryParams.append('until', params.until);
      if (params.event) queryParams.append('event', params.event);
      if (params.refDocType)
        queryParams.append('refDocType', params.refDocType);
      if (params.unitOfMeasure)
        queryParams.append('unitOfMeasure', params.unitOfMeasure);
      if (params.transactionId)
        queryParams.append('transactionId', params.transactionId);
      if (params.originLocation)
        queryParams.append('originLocation', params.originLocation);

      if (params.lotCode) {
        if (Array.isArray(params.lotCode)) {
          params.lotCode.forEach(code => queryParams.append('lotCode', code));
        } else {
          queryParams.append('lotCode', params.lotCode);
        }
      }

      if (params.refDocNumber) {
        if (Array.isArray(params.refDocNumber)) {
          params.refDocNumber.forEach(number =>
            queryParams.append('refDocNumber', number)
          );
        } else {
          queryParams.append('refDocNumber', params.refDocNumber);
        }
      }

      if (params.product) {
        if (Array.isArray(params.product)) {
          params.product.forEach(prod => queryParams.append('product', prod));
        } else {
          queryParams.append('product', params.product);
        }
      }

      if (params.columns && params.columns.length > 0) {
        params.columns.forEach(col => queryParams.append('columns', col));
      }

      // Get current locale from i18n library
      const currentLocale = i18n.language;

      queryParams.append('locale', currentLocale);

      const blob = await fetchBlobFromApi(
        `${REACT_APP_DARWIN_URL}/reports/${selectedCompany}/transactions-report?${queryParams.toString()}`
      );

      const todayIso = new Date().toISOString();
      const datePart = formatDate(todayIso, 'yyyy-MM-dd') || 'unknown-date';
      const extension =
        (params.format === 'excel' ? 'xlsx' : params.format) || 'pdf';
      const filename = `Transactions_Report_${datePart}.${extension}`;

      downloadBlob(blob, filename);
      return blob;
    },
  });
};

/**
 * Delete a transaction.
 */
export const useDeleteTransaction = () => {
  const queryClient = useQueryClient();
  const { selectedCompany } = useUI();

  return useMutation({
    mutationFn: async ({ transactionId }: { transactionId: string }) => {
      return fetchFromApi<Report>(
        `${REACT_APP_DARWIN_URL}/inventories/${selectedCompany}/transaction/${transactionId}`,
        {
          method: 'DELETE',
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [REPORTS_QUERY_KEY],
      });
    },
  });
};

export const useGetFiltersData = () => {
  const { selectedCompany } = useUI();
  return useQuery({
    queryKey: [REPORTS_FILTERS_QUERY_KEY, selectedCompany],
    queryFn: () =>
      fetchFromApi<ReportsFiltersResponse>(
        `${REACT_APP_DARWIN_URL}/inventories/${selectedCompany}/filters`
      ),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });
};
